import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check, Lock, ArrowRight } from "lucide-react";
import { API } from "@/lib/auth";
import { Eyebrow } from "@/components/bridal/BridalSections";

const pretty = (iso) => {
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso || "") ? new Date(`${iso}T00:00:00`) : null;
  return d ? d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : iso;
};

function useDepositInfo(token, sessionId) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(!!sessionId);
  useEffect(() => {
    let tries = 0;
    let timer;
    const load = () => axios.get(`${API}/bridal/deposit/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(e?.response?.data?.detail || "This payment link is not valid"));
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/bridal/deposit/status/${sessionId}`);
        if (r.data.paid || ++tries > 10) { setChecking(false); load(); return; }
      } catch { setChecking(false); load(); return; }
      timer = setTimeout(poll, 2000);
    };
    if (sessionId) poll(); else load();
    return () => clearTimeout(timer);
  }, [token, sessionId]);
  return { info, error, checking };
}

const Shell = ({ children }) => (
  <div data-testid="page-bridal-pay" className="min-h-screen bg-[#FAF7F2] text-[#2A1F27] pt-36 pb-24 px-5">
    <div className="mx-auto max-w-xl">{children}</div>
  </div>
);

export default function BridalPay() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const { info, error, checking } = useDepositInfo(token, params.get("session_id"));
  const [busy, setBusy] = useState(false);

  const pay = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bridal/deposit/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment could not be started — please try again");
      setBusy(false);
    }
  };

  if (error) return <Shell><p data-testid="bridal-pay-error" className="font-display text-3xl">{error}</p><p className="mt-4 text-sm text-[#2A1F27]/60">Please contact Malek directly and she'll send you a new link.</p></Shell>;
  if (!info || checking) return <Shell><div className="flex items-center gap-3 text-sm text-[#2A1F27]/60" data-testid="bridal-pay-loading"><Loader2 className="animate-spin" size={18} /> {checking ? "Confirming your payment…" : "Loading…"}</div></Shell>;

  if (info.paid) {
    return (
      <Shell>
        <div data-testid="bridal-pay-success">
          <div className="w-14 h-14 border border-[#B89A5E] rounded-full flex items-center justify-center"><Check size={20} className="text-[#9C7F45]" /></div>
          <div className="mt-8"><Eyebrow>{info.kind === "balance" ? "Balance received" : "Booking fee received"}</Eyebrow></div>
          <h1 className="font-display text-4xl sm:text-5xl mt-6 leading-[1.08]">
            {info.kind === "balance" ? `Thank you, ${info.first_name}. You're paid in full.` : `Thank you, ${info.first_name}. Your date is secured.`}
          </h1>
          <p className="mt-6 text-sm sm:text-base leading-[1.9] text-[#2A1F27]/70">
            {info.kind === "balance"
              ? <>Your balance for <strong>{pretty(info.wedding_date)}</strong> has been received. Now all that's left is to enjoy the build-up to your day.</>
              : <>Your booking fee for <strong>{pretty(info.wedding_date)}</strong> has been received. Malek will be in touch personally with the next steps.</>}
          </p>
          <div className="mt-6 text-[10px] uppercase tracking-[0.28em] text-[#2A1F27]/50">Reference {info.reference}</div>
          <Link to="/bridal" className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] border-b border-[#2A1F27]/30 pb-1">Back to Bridal <ArrowRight size={12} /></Link>
        </div>
      </Shell>
    );
  }

  if (info.unavailable) return <Shell><p data-testid="bridal-pay-unavailable" className="font-display text-3xl">This booking is no longer available.</p><p className="mt-4 text-sm text-[#2A1F27]/60">Please contact Malek directly.</p></Shell>;

  return (
    <Shell>
      <Eyebrow>{info.kind === "balance" ? "Your remaining balance" : "Secure your wedding date"}</Eyebrow>
      <h1 className="font-display text-4xl sm:text-5xl mt-6 leading-[1.08]">
        {info.kind === "balance" ? `${info.first_name}, your day is almost here.` : `${info.first_name}, let's make it official.`}
      </h1>
      <p className="mt-6 text-sm sm:text-base leading-[1.9] text-[#2A1F27]/70">
        {info.kind === "balance"
          ? <>This is the remaining balance for your bridal hair on <strong>{pretty(info.wedding_date)}</strong>. Once paid, your booking is complete.</>
          : <>Your wedding date of <strong>{pretty(info.wedding_date)}</strong> is available. A booking fee of £{Number(info.amount).toFixed(2).replace(".00", "")} secures it in Malek's diary.
            Your date is confirmed once the fee is received and you've had your written confirmation.</>}
      </p>
      <div className="mt-10 border-y border-[#2A1F27]/10 py-6 flex items-baseline justify-between">
        <span className="text-[10px] uppercase tracking-[0.28em] text-[#2A1F27]/60">{info.kind === "balance" ? "Bridal balance" : "Bridal booking fee"} · Ref {info.reference}</span>
        <span data-testid="bridal-pay-amount" className="font-display text-3xl">£{Number(info.amount).toFixed(2).replace(".00", "")}</span>
      </div>
      <button data-testid="bridal-pay-btn" onClick={pay} disabled={busy}
        className="mt-10 w-full inline-flex items-center justify-center gap-3 bg-[#2A1F27] text-[#FAF7F2] px-10 py-5 text-xs uppercase tracking-[0.24em] transition-colors duration-300 hover:bg-[#9C7F45] disabled:opacity-60">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={13} />} Pay securely
      </button>
      <p className="mt-4 text-[11px] text-center text-[#2A1F27]/45">Payments are processed securely by Stripe.</p>
    </Shell>
  );
}
